import { Asset } from 'app/shared/collections/asset';
import { AssetCollection } from 'app/shared/collections/asset_collection.service';
import { IpDevice } from 'app/shared/collections/ip_device';
import { AnyFieldSchemaType, FieldSchemaUnits } from 'app/shared/collections/StatDefinition';
import { ImsTimeCollection } from 'app/shared/collections/time/ims_time_collection.factory';
import { Zone } from 'app/shared/collections/zones/zone';
import { Operator } from 'app/shared/operators';
import { UnitService } from 'app/shared/unit.service';
import { julianToUnix } from 'app/shared/utilities/time';
import { IMSAggregator } from 'app/views/aggregators/aggregator';
import { IMSValueGetter, ValueGetterValueType } from 'app/views/value_getters/value_getter';
import { isNil } from 'lodash';

function recurseRelationshipCollection(
    assetCollection: AssetCollection,
    relationshipCollection: ImsTimeCollection,
    relationshipColumn: string,
    initialParent: Asset,
    time: number,
): Array<number> {
    // this is very non-performant because we're searching backwards, rather than knowing forwards
    //  at some stage we'll surely want to build the reverse looks like we do/did for normal
    //  (non-temporal) IMS object relationships eg, >>> asset.ipDevices.type, etc.
    // for now, this will do I guess...

    const matchedAssets = new Set<number>();
    const queue = [initialParent.id];
    const assets = assetCollection.getAllAsArray();

    // this should ensure our own stats end up considered
    matchedAssets.add(initialParent.id);

    while (queue.length) {
        const parentId = queue.pop();
        for (const asset of assets) {
            const stats = relationshipCollection.getStatsUnix(asset.id, time);
            for (const stat of stats) {
                if (stat[relationshipColumn] === parentId && !matchedAssets.has(asset.id)) {
                    // we've found an asset which is contained in the searched parent,
                    //  and we haven't seen this path before; now we can keep track of it
                    //  and also search its children; oh boy
                    matchedAssets.add(asset.id);
                    queue.push(asset.id);
                }
            }
        }
    }

    return Array.from(matchedAssets);
}

function collectAndAggregateStats(
    assetsIds: Array<number>,
    time: number,
    aggregator: IMSAggregator,
    statCollection: ImsTimeCollection,
    statKey: string,
): number | string | null | undefined {
    const stats = assetsIds.map((assetId) => statCollection.getStatsUnix(assetId, time)).flat();
    const values = stats.map((stat) => stat[statKey]);
    return aggregator.getAggValueForValueArray(values);
}

export class AssetFollowRelationshipStatValueGetter extends IMSValueGetter {
    public readonly operators: Operator[];
    public readonly placeholder: string;
    public readonly units: FieldSchemaUnits;
    public readonly statCollection: ImsTimeCollection;
    private readonly relationshipCollection: ImsTimeCollection;
    private readonly assetCollection: AssetCollection;
    private readonly relationshipColumn: string;
    private readonly sourceAssetTypeNames: Array<string>;
    private readonly fieldSchemaType: AnyFieldSchemaType;

    constructor(
        operators: Operator[],
        placeholder: string,
        units: FieldSchemaUnits,
        statKey: string,
        fieldSchemaType: AnyFieldSchemaType,
        relationshipCollection: ImsTimeCollection,
        statCollection: ImsTimeCollection,
        assetCollection: AssetCollection,
        relationshipColumn: string,
        sourceAssetTypeNames: Array<string>,
    ) {
        super(statCollection, statKey, units, null, [], operators, placeholder, null);

        this.operators = operators;
        this.placeholder = placeholder;
        this.units = units;
        this.fieldSchemaType = fieldSchemaType;
        this.relationshipCollection = relationshipCollection;
        this.statCollection = statCollection;
        this.assetCollection = assetCollection;
        this.relationshipColumn = relationshipColumn;
        this.sourceAssetTypeNames = sourceAssetTypeNames || null;
    }

    getFieldType(): AnyFieldSchemaType {
        return this.fieldSchemaType;
    }

    getFormattedValueForAsset(
        asset: Asset,
        time: Cesium.JulianDate,
        aggregator: IMSAggregator,
    ): string {
        const value = this.getValueForAsset(asset, time, aggregator);

        if (isNil(value)) {
            return null;
        }

        const { value: v, unit } = UnitService.convertValue(this.units, value as any);

        return `${v}${unit}`;
    }

    getFormattedValueForIPDevice(
        ipDevice: IpDevice,
        time: Cesium.JulianDate,
        agg: IMSAggregator,
    ): string {
        return '';
    }

    getFormattedValueForZone(zone: Zone, time: Cesium.JulianDate, agg: IMSAggregator): string {
        return '';
    }

    getValueForAsset(
        asset: Asset,
        time: Cesium.JulianDate,
        aggregator: IMSAggregator,
    ): ValueGetterValueType {
        const t = julianToUnix(time);

        let assetsIds;

        // not-empty = explicit, empty = nothing, null = gratuitous
        if (!this.sourceAssetTypeNames || this.sourceAssetTypeNames.includes(asset.type?.name)) {
            assetsIds = recurseRelationshipCollection(
                this.assetCollection,
                this.relationshipCollection,
                this.relationshipColumn,
                asset,
                t,
            );
        } else {
            assetsIds = [asset.id];
        }

        return collectAndAggregateStats(
            assetsIds,
            t,
            aggregator,
            this.statCollection,
            this.statKey,
        );
    }

    getValueForIPDevice(
        ipDevice: IpDevice,
        time: Cesium.JulianDate,
        aggregator: IMSAggregator,
    ): ValueGetterValueType {
        return undefined;
    }

    // TODO: electing to use the getValueForStat from the base class for now- is this problematic?
    // getValueForStat(stat, aggregator: IMSAggregator): ValueGetterValueType {
    //     return undefined;
    // }

    getValueForZone(
        zone: Zone,
        time: Cesium.JulianDate,
        aggregator: IMSAggregator,
    ): ValueGetterValueType {
        return undefined;
    }
}
